import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Trash2, Edit } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RolePermissionsSheet } from "./RolePermissionsSheet";
import { RoleApi } from "@/api";
import { useContext } from "react";

interface Permission {
  id: number;
  permissionCode: string;
  permissionName: string;
  permissionType: string;
  featureVO: {
    id: number;
    featureCode: string;
    featureName: string;
  };
}

interface FeaturePermissions {
  featureId: number;
  featureCode: string;
  featureName: string;
  createPermission?: Permission;
  viewPermission?: Permission;
  updatePermission?: Permission;
  deletePermission?: Permission;
}

interface Role {
  id: string;
  name: string;
  description: string;
  users?: number;
  permissions?: FeaturePermissions[];
}

interface RoleTableProps {
  roles: Role[];
  onDeleteRole: (roleId: string) => void;
  refetchRoles: () => void;
  possiblePermissions: {
    featureId: number;
    featureCode: string;
    featureName: string;
  }[];
}

export const RoleTable: React.FC<RoleTableProps> = ({
  roles,
  onDeleteRole,
  refetchRoles,
  possiblePermissions,
}) => {
  const [isPermissionsSheetOpen, setIsPermissionsSheetOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const handleEditClick = async (role: Role) => {
    setPermissionsLoading(true);
    try {
      const res = await RoleApi.getPermissionsUsingRoleId(role.id);
      const permissions = Array.isArray(res.data) ? res.data : [];
      setSelectedRole({ ...role, permissions });
      setIsPermissionsSheetOpen(true);
    } catch (e) {
      setSelectedRole({ ...role, permissions: [] });
      setIsPermissionsSheetOpen(true);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleDeleteClick = (roleId: string) => {
    setRoleToDelete(roleId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (roleToDelete) {
      onDeleteRole(roleToDelete);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Role</TableHead>
            <TableHead>Users</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                No roles found.
              </TableCell>
            </TableRow>
          ) : (
            roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-50 p-2 rounded-full">
                      <ShieldCheck className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">{role.name}</div>
                      <div className="text-sm text-gray-500">
                        {role.description}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {role.users ?? 0}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(role)}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    {role.name !== "Administrator" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(role.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {selectedRole && (
        <RolePermissionsSheet
          role={selectedRole}
          open={isPermissionsSheetOpen}
          onOpenChange={setIsPermissionsSheetOpen}
          onPermissionsSaved={refetchRoles}
          possiblePermissions={possiblePermissions}
        />
      )}

      {permissionsLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-20 z-50">
          <div className="bg-white p-6 rounded shadow text-center">
            Loading permissions...
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this role. Users with this role will
              need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
